// Wave 15 — handles the redirect from the backend's SSO callback.
//
// The backend mints a one-time JWT (scope=``sso-session``, 60s TTL),
// redirects the browser here with ``#token=…&provider=…&next=…``, and
// this component:
//   1. Pulls the token out of the URL fragment (so it never lands in
//      browser history / Referer headers / server logs).
//   2. POSTs it to ``/api/auth/sso/exchange`` to swap for a real
//      access token.
//   3. Persists the token via the same ``applySsoSession`` helper the
//      ``AuthProvider`` exposes, then routes to ``next`` (default
//      ``/dashboard``).
//
// A3 (Keymaster#2) — SSO must not bypass app-level 2FA. When the account
// has TOTP enabled the exchange replies ``requires_2fa`` + a
// ``challenge_token`` instead of an access token; we then prompt for the
// TOTP/backup code and finish through the very same ``verify2fa`` path the
// password login uses (``POST /api/auth/2fa/login``).
//
// Failure modes (expired token, tampered, user vanished) clear the
// hash and bounce back to ``/auth`` with an error so the user can
// retry via the password form or another SSO button.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { useT } from '../i18n';
import { BACKEND_URL } from '../runtimeConfig';

const API = `${BACKEND_URL}/api`;

function parseHashParams() {
  if (typeof window === 'undefined') return new URLSearchParams();
  const hash = (window.location.hash || '').replace(/^#/, '');
  return new URLSearchParams(hash);
}

export default function SsoCallback() {
  const { applySsoSession, verify2fa } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [challenge, setChallenge] = useState(''); // A3: SSO 2FA challenge token
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const nextRef = useRef('/dashboard');
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const params = parseHashParams();
    const sessionToken = params.get('token');
    const next = params.get('next') || '/dashboard';
    nextRef.current = next.startsWith('/') ? next : '/dashboard';
    if (!sessionToken) {
      setError(t('Missing SSO session token'));
      return;
    }

    // Strip the hash immediately so a back-button doesn't replay.
    try {
      window.history.replaceState(null, '', window.location.pathname);
    } catch (_e) {
      /* no-op */
    }

    axios
      .post(`${API}/auth/sso/exchange`, { session_token: sessionToken })
      .then((r) => {
        const { access_token, user, requires_2fa, challenge_token } = r.data || {};
        // A3 — account has app-level 2FA; SSO does not skip it. Prompt for the
        // second factor and complete via the shared /auth/2fa/login flow.
        if (requires_2fa && challenge_token) {
          setChallenge(challenge_token);
          return;
        }
        if (!access_token) {
          setError(t('Invalid SSO session'));
          return;
        }
        applySsoSession(access_token, user);
        toast.success(t('Signed in successfully!'));
        navigate(nextRef.current, { replace: true });
      })
      .catch((err) => {
        setError(err.response?.data?.detail || t('SSO sign-in failed'));
      });
  }, [applySsoSession, navigate, t]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!challenge || !code.trim() || submitting) return;
    setError('');
    setSubmitting(true);
    const ok = await verify2fa(challenge, code.trim());
    setSubmitting(false);
    if (ok) {
      navigate(nextRef.current, { replace: true });
    } else {
      setError(t('Invalid 2FA code'));
    }
  };

  return (
    <div className="xp-app">
      <main className="xp-main">
        <section className="xp-auth-main">
          <div className="xp-auth-wrap">
            <div className="xp-card">
              <div className="xp-card-body">
                {challenge ? (
                  <>
                    <h1 className="xp-h1">{t('Two-factor code')}</h1>
                    <form onSubmit={handleVerify} noValidate>
                      <div className="xp-f">
                        <input
                          type="text"
                          value={code}
                          onChange={(e) => setCode(e.target.value)}
                          placeholder="123456 or BACKUP-CODE9"
                          inputMode="text"
                          autoComplete="one-time-code"
                          autoFocus
                          required
                          data-testid="sso-2fa-code"
                        />
                        <div className="xp-help">
                          {t(
                            'Enter the 6-digit code from your authenticator app, or one of your backup codes.',
                          )}
                        </div>
                        {error ? <div className="xp-help">⚠ {error}</div> : null}
                      </div>
                      <button
                        type="submit"
                        className="xp-btn"
                        disabled={submitting || !code.trim()}
                      >
                        {submitting ? t('One moment…') : t('Sign in')}
                      </button>
                    </form>
                  </>
                ) : (
                  <>
                    <h1 className="xp-h1">{error ? t('SSO failed') : t('Signing in…')}</h1>
                    <p className="xp-card-sub">
                      {error ? (
                        <>
                          <span>⚠ {error}</span>
                          <br />
                          <a
                            href="/auth"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate('/auth', { replace: true });
                            }}
                          >
                            {t('Back to sign-in')}
                          </a>
                        </>
                      ) : (
                        t('One moment…')
                      )}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
